import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CfELogo = "https://customer-assets.emergentagent.com/job_startup-central-3/artifacts/3g256hrq_CfE%20Logo.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Startups', path: '/startups' },
  { name: 'Events', path: '/events' },
  { name: 'Book an Advisor', path: '/advisors' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-gray-100' 
          : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <nav className="container-main">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="navbar-logo">
            <div className={`rounded-lg transition-all duration-300 ${!isScrolled ? 'bg-white/95 p-1.5' : ''}`}>
              <img 
                src={CfELogo} 
                alt="Centre for Entrepreneurship - Krea University" 
                className="h-16 md:h-20 w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  location.pathname === link.path
                    ? isScrolled 
                      ? 'text-[#075FA3] bg-[#075FA3]/5'
                      : 'text-white bg-white/15'
                    : isScrolled
                      ? 'text-[#58595B] hover:text-[#075FA3] hover:bg-gray-50'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/register" data-testid="nav-register-btn">
              <Button className="bg-[#C83041] hover:bg-[#A92332] text-white rounded-full px-6 py-2.5 font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                Register Your Startup
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${isScrolled ? 'hover:bg-gray-100' : 'hover:bg-white/10'}`}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-[#231F20]' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-[#231F20]' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden bg-white border-t border-gray-100"
              data-testid="mobile-menu"
            >
              <div className="py-4 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-3 rounded-lg font-medium transition-all ${
                      location.pathname === link.path
                        ? 'text-[#075FA3] bg-[#075FA3]/5'
                        : 'text-[#58595B] hover:text-[#075FA3] hover:bg-gray-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="pt-4 px-4">
                  <Link to="/register" className="block">
                    <Button className="w-full bg-[#C83041] hover:bg-[#A92332] text-white rounded-full py-3 font-semibold">
                      Register Your Startup
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
